"use client";
import { Box } from "@dxp-feed/ui/Box";
import { Button } from "@dxp-feed/ui/Button";
import {
  useState,
  useEffect,
  useCallback,
  useAppSelector,
} from "@/utils/hooks/reactUseCustomHooks";
import { KeyboardEvent, MouseEvent } from "react";
import { RootState } from "@/utils/redux/store";

// Interfaces for Props
interface AnimatedQuantityButtonProps {
  isGlobalSearch?: boolean;
  setDropDown?: (value: boolean) => void;
  productQuantity?: number;
  maxQuantity?: number;
  className?: string;
  isFullView?: boolean;
  defaultFullView?: boolean;
  onDeleteBtnClick?: (
    e: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
  ) => void;
  clickCallbackHandler?: (quantity: number) => void;
  addListBtn?: boolean;
  onQuantityChange?: (quantity: number) => void;
  isUserLoggedOut?: boolean;
  shopPopUp?: boolean;
  disabledDelete?: boolean;
  isIncrementDisabled?: boolean;
  isDecrementDisabled?: boolean;
  disabled?: boolean;
  isBtnOutline?: boolean;
  brand?:boolean;
  inStock?:boolean;
}

// Base Button Component
const BaseButton: React.FC<{
  onClick: React.MouseEventHandler<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  className?: string;
  disabled?: boolean;
  iconClass: string;
  ariaLabel: string;
  isGlobalSearch?: boolean;
  setDropDown?: (value: boolean) => void;
  isBtnOutline?: boolean;

}> = ({
  onClick,
  onKeyDown,
  className,
  disabled,
  iconClass,
  ariaLabel,
  setDropDown,
  isBtnOutline,
}) => {
  return (
    <Button
      type="button"
      variant="icon"
      className={`min-w-[34px] h-[34px] flex items-center justify-center relative [&:before]:hidden rounded-full max-w-[34px] max-h-[34px] ${className ?? ""}`}
      onClick={!disabled ? onClick : undefined}
      onKeyDown={!disabled ? onKeyDown : undefined}
      aria-label={ariaLabel}
      onMouseLeave={(e: MouseEvent<HTMLButtonElement>) => {
        e.currentTarget.blur();
        if (setDropDown) {
          setDropDown(true);
        }
      }}
    >
      <Box
        as="span"
        className={`${iconClass} ${isBtnOutline ? "text-brandSecondary" : "text-brandWhite"} w-[30px] h-[30px] rounded-full flex items-center justify-center`}
        aria-hidden="true"
      />
    </Button>
  );
};

const IncrementButton: React.FC<{
  onClick: React.MouseEventHandler<HTMLElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
  disabled?: boolean;
  isGlobalSearch?: boolean;
  setDropDown?: (value: boolean) => void;
  isBtnOutline?: boolean;
}> = (props) => (
  <BaseButton {...props} iconClass="icon-plus" ariaLabel="Increment quantity" />
);

const DecrementButton: React.FC<{
  onClick: React.MouseEventHandler<HTMLElement>;
  onKeyDown: React.KeyboardEventHandler<HTMLElement>;
  quantity?: number;
  disabled?: boolean;
  className?: string;
  isGlobalSearch?: boolean;
  setDropDown?: (value: boolean) => void;
  isBtnOutline?: boolean;
}> = ({ quantity, ...props }) => (
  <BaseButton
    {...props}
    iconClass={quantity === 1 ? `icon-bin` : `icon-minus`}
    ariaLabel={quantity === 1 ? "Remove item" : "Decrement quantity"}
  />
);

// Quantity Display Component
const QuantityDisplay: React.FC<{
  prevQuantity: number | null;
  quantity: number;
  keyId: number;
  isBtnOutline?: boolean;
}> = ({ prevQuantity, quantity, keyId, isBtnOutline }) => (
  <Box
    as="div"
    className="relative overflow-hidden flex items-center justify-center h-[40px] min-w-[52px]"
    aria-live="polite"
    aria-atomic="true"
  >
    <Box as="span" className="sr-only">
      Current quantity: {quantity}
    </Box>
    {prevQuantity !== null && (
      <Box
        key={`old-${keyId}`}
        className={`absolute w-full text-center animate-slide-down ${isBtnOutline ? "text-brandSecondary" : "text-brandWhite"} font-bold`}
        aria-hidden="true"
      >
        {prevQuantity == 0 ? "" : prevQuantity}
      </Box>
    )}
    <Box
      key={`new-${keyId}`}
      className={`absolute w-full text-center animate-slide-up ${isBtnOutline ? "text-brandSecondary" : "text-brandWhite"} font-bold`}
      aria-hidden="true"
    >
      {quantity}
    </Box>
  </Box>
);

const AnimatedQuantityButton: React.FC<AnimatedQuantityButtonProps> = ({
  productQuantity = 0,
  maxQuantity = 99,
  className = "",
  onDeleteBtnClick,
  addListBtn = false,
  onQuantityChange,
  clickCallbackHandler,
  shopPopUp = false,
  isFullView = false,
  defaultFullView = false,
  disabledDelete = false,
  isIncrementDisabled,
  isDecrementDisabled,
  disabled = false,
  isGlobalSearch,
  setDropDown,
  isBtnOutline,
  brand,
  inStock
}) => {
  const { loggedIn: userLoggedIn, uid: Uid } = useAppSelector(
    (state: RootState) => state.gigyaslice
  );

  const { productDetail }: any = useAppSelector(
    (state) => state?.getProductDetailsDataSlice
  );

  const [quantity, setQuantity] = useState<number>(productQuantity);
  const [prevQuantity, setPrevQuantity] = useState<number | null>(null);
  const [key, setKey] = useState(0);
  const [showAddListBtn, setShowAddListBtn] = useState(addListBtn);
  const [isTouched, setIsTouched] = useState(false); // New state for touch handling
  const [showDots, setShowDots] = useState(false);
  const handleIncrement = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      if ("key" in e && e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      e.stopPropagation();
      setShowDots(false);
      if (shopPopUp) {
        clickCallbackHandler?.(quantity);
        return;
      }
      if (quantity < maxQuantity) {
        if (userLoggedIn || !defaultFullView) {
          setPrevQuantity(quantity);
          setQuantity((prevQty) => {
            const newQuantity = Math.min(prevQty + 1, maxQuantity);
            onQuantityChange?.(newQuantity);
            return newQuantity;
          });
          setKey((prev) => prev + 1);
        }
        clickCallbackHandler?.(Math.min(quantity + 1, maxQuantity));
      }
    },
    [
      quantity,
      maxQuantity,
      shopPopUp,
      onQuantityChange,
      clickCallbackHandler,
      userLoggedIn,
      defaultFullView,
    ]
  );

  const handleDecrement = useCallback(
    (e: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) => {
      if ("key" in e && e.key !== "Enter" && e.key !== " ") return;

      e.preventDefault();
      e.stopPropagation();

      if (quantity === 1) {
        if ("button" in e || ("key" in e && e.key === "Enter")) {
          if (isFullView) {
            setShowDots(true);
          }
          onDeleteBtnClick?.(
            e as
              | React.MouseEvent<HTMLElement>
              | React.KeyboardEvent<HTMLElement>
          );
        }
        setPrevQuantity(quantity);
        if (quantity === 1 && disabledDelete) {
          return;
        } else {
          setQuantity(quantity - 1);
        }
        setKey((prev) => prev + 1);
      } else if (quantity > 0) {
        setPrevQuantity(quantity);
        setQuantity((prev) => {
          const newQuantity = Math.max(0, prev - 1);
          onQuantityChange?.(newQuantity);
          return newQuantity;
        });
        setKey((prev) => prev + 1);
      }
      clickCallbackHandler?.(Math.max(0, quantity - 1));
    },
    [
      quantity,
      onDeleteBtnClick,
      onQuantityChange,
      clickCallbackHandler,
      disabledDelete,
    ]
  );
  // Touch handlers
  const handleTouchStart = useCallback(() => {
    setIsTouched(true);
  }, []);
  const handleTouchEnd = useCallback(() => {
    setTimeout(() => {
      setIsTouched(false);
    }, 2000);
  }, []);

  const defaultView = defaultFullView && "fullViewBtn";
  const fullView = isFullView && quantity > 0 && "fullView";
  const addListBtnView = showAddListBtn && quantity > 0 && "addListBtn";

  // Determine if text is visible (not clipped by overflow)
  const isTextVisible =
    defaultFullView || isFullView || (showAddListBtn && quantity > 0);

  useEffect(() => {
    setQuantity(productQuantity);
  }, [productQuantity]);

  // Render "Add To List" button when quantity is 0
  if (quantity <= 0 && (addListBtn || (addListBtn && isFullView))) {
    return (
      <Box
        as="div"
        id="add_to_cart_product"
        className={`${className} flex animated-quantity-button`}
      >
        <Button
          onClick={handleIncrement}
          onKeyDown={handleIncrement}
          type="button"
          variant="primary"
          aria-label="Add to list"
          disabled={disabled}
          className={`${
            disabled
              ? "cursor-not-allowed bg-brandSecondary border-brandSecondary hover:border-brandSecondary focus:border-brandSecondary hover:bg-brandSecondary focus:bg-brandSecondary opacity-70"
              : ""
          } md:w-[220px] w-full h-[40px] font-normal [&:before]:hidden !text-white min-w-[110px] 3xs:min-w-[160px] xs:min-w-[228px] md:min-w-[220px] lgs:min-w-[130px] 2lg:min-w-[220px] 2lg:max-w-[220px] transition-all ease-in-out duration-700 max-2xs:text-body overflow-visible ${showDots ? "min-w-[130px]" : ""}`}
        >
          {"Add to list"}
        </Button>
      </Box>
    );
  }

  // Render quantity controls when quantity > 0 or when not showing add list button
  return (
    <Box
      as="div"
      id="add_to_cart_product"
      className={`${className} ${fullView} flex animated-quantity-button `}
      role="group"
      aria-label="Product quantity controls"
      onClick={(e: any) => e.stopPropagation()}
    >
      <Box
        as="div"
        className={`${isTouched ? '!w-[130px]' :''} ${fullView} ${addListBtnView} ${defaultView} ${quantity > 0 ? "justify-center" : "justify-start"} p-0 flex items-center w-[40px] h-[40px] [&.addListBtn]:w-[130px] [&.fullView]:w-[130px] [&.fullViewBtn]:w-[130px] overflow-hidden ${
          !disabled ? "hover:w-[130px] focus-within:w-[130px]" : ""
        } ${
          isTouched && !disabled ? "w-[130px]" : ""
        } focus-within:outline focus-within:outline-2 focus-within:outline-brandSecondary transition-all duration-700 ${isBtnOutline ? "border border-brandSecondary" : "bg-brandSecondary"}  rounded-full ${
          disabled ? "cursor-not-allowed opacity-100" : ""
        } [clip-path:view-box]`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {quantity > 0 ? (
          <>
            <DecrementButton
              isGlobalSearch={isGlobalSearch}
              setDropDown={setDropDown}
              onKeyDown={handleDecrement}
              className={
                disabledDelete && quantity === 1
                  ? "cursor-not-allowed [&>span]:!text-grey500 [&>span]:!text-xl"
                  : ""
              }
              quantity={quantity}
              onClick={(e) => {
                if (disabledDelete && quantity === 1) {
                  return;
                }
                handleDecrement(e);
              }}
              disabled={
                isDecrementDisabled || (disabledDelete && quantity === 1)
              }
              isBtnOutline={isBtnOutline}
            />
            <QuantityDisplay
              prevQuantity={prevQuantity}
              quantity={quantity}
              keyId={key}
              isBtnOutline={isBtnOutline}
            />
          </>
        ) : (
          !isFullView && (
            <Button
              type="button"
              variant="primary"
              onClick={handleIncrement}
              onKeyDown={handleIncrement}
              aria-label="Add to list"
              disabled={disabled || isIncrementDisabled}
              className={`${defaultView} ${
                disabled || isIncrementDisabled
                  ? "border-brandSecondary hover:border-brandSecondary focus:border-brandSecondary opacity-100 bg-brandSecondary"
                  : ""
              } overflow-visible opacity-100 add-animated-btn [&.fullViewBtn]:translate-x-[-15px] addText text-brandWhite cursor-pointer flex items-center min-h-10 gap-1 whitespace-nowrap transition-all duration-700 h-full justify-start px-0 [&:before]:hidden ${
                !disabled && !isIncrementDisabled
                  ? "hover:translate-x-[-35px] focus-within:translate-x-[-35px]"
                  : ""
              } ${
                isTouched && !disabled && !isIncrementDisabled
                  ? "hover:translate-x-[-35px] focus-within:translate-x-[-35px]  !bg-brandSecondary !border-brandSecondary opacity-100"
                  : "!bg-brandSecondary !border-brandSecondary opacity-100"
              } ${brand === true && inStock === false ? "!bg-brandBlack !border-brandBlack":""} `}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <Box
                as="span"
                className={`${disabled || isIncrementDisabled ? "cursor-not-allowed" : ""} icon-plus w-10 min-w-10 ${isBtnOutline ? "text-brandSecondary" : "text-white"} -ml-[1px]`}
                aria-hidden="true"
              ></Box>
              <Box
                as="span"
                className={`inline-flex ${isBtnOutline ? "text-brandSecondary" : "text-white"}`}
              >
                {"Add to list"}
              </Box>
              <Box
                as="span"
                className={`icon-plus w-10 min-w-10 ${isBtnOutline ? "text-brandSecondary" : "text-white"} relative right-[7px]`}
                aria-hidden="true"
              ></Box>
            </Button>
          )
        )}
        {quantity > 0 && (
          <IncrementButton
            isGlobalSearch={isGlobalSearch}
            setDropDown={setDropDown}
            onKeyDown={handleIncrement}
            onClick={handleIncrement}
            disabled={
              quantity >= maxQuantity || isIncrementDisabled || disabled
            }
            isBtnOutline={isBtnOutline}
          />
        )}
        {showDots && (
          <Box
            as="div"
            className="flex items-center rounded-full overflow-hidden min-w-[130px] h-10 w-full"
          >
            <Box
              as="div"
              className="flex gap-3 justify-center w-full items-center relative top-[3px]"
            >
              <Box as="span" className="sr-only">
                Loading...
              </Box>
              <Box
                as="div"
                className={`h-[10px] w-[10px] ${isBtnOutline ? 'bg-brandSecondary': 'bg-white'}  rounded-full animate-bounce [animation-delay:-0.3s]`}
              ></Box>
              <Box
                as="div"
                className={`h-[10px] w-[10px] ${isBtnOutline ? 'bg-brandSecondary': 'bg-white'}  rounded-full animate-bounce [animation-delay:-0.15s]`}
              ></Box>
              <Box
                as="div"
                className={`h-[10px] w-[10px] ${isBtnOutline ? 'bg-brandSecondary': 'bg-white'}  rounded-full animate-bounce`}
              ></Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AnimatedQuantityButton;
